<script>
  import FingerprintJS from '@fingerprintjs/fingerprintjs'
  import { get, call } from 'vuex-pathify'
  //   import router from '@/router'
  //   import { get, call } from 'vuex-pathify'
  //   import screen from '@/helpers/screen'
  import createGuardMixin from '@/helpers/createGuardMixin'

  const guardMixin = createGuardMixin(
    async ({ store, redirect, route }) => {
      let isLoggedIn
      try {
        isLoggedIn = await store.dispatch('account/loggedInAsync')
      } catch {
        isLoggedIn = false
      }
      if (!isLoggedIn) {
        return redirect({
          name: 'AccountLogin',
          query: { returnPath: route.fullPath },
        })
      }
    },
    ['loggedIn']
  )

  export default {
    name: 'SendABox',
    mixins: [guardMixin],
    data() {
      return {
        formData: {
          friendFirstName: '',
          friendLastName: '',
          friendEmail: '',
          message: '',
        },
        isLoading: false,
        submitStatus: null,
        visitorId: null,
        remainingBoxes: null,
        nextAvailableDate: null,
        isLoadingReferrals: true,
      }
    },
    computed: {
      ...get('prismic', ['sendABoxData']),
      sendABoxContent() {
        return this.sendABoxData || ''
      },
      referralMessage() {
        if (this.isLoadingReferrals || this.remainingBoxes === null) {
          return ''
        }

        if (this.remainingBoxes > 0) {
          return `<div style="margin-bottom: 62px">You can send ${
            this.remainingBoxes
          } more intro box${
            this.remainingBoxes !== 1 ? 'es' : ''
          }!  Please submit them one at a time</div>`
        } else {
          return `<div style="background-color: #f4b857; padding: 10px; border-radius: 5px;margin-bottom: 62px">You have reached your limit of 5 boxes.</div>`
        }
      },
    },
    async created() {
      this.loadSendABoxData()
      await this.loadUserReferralCount()
      try {
        const fp = await FingerprintJS.load()
        const result = await fp.get()
        this.visitorId = result.visitorId
      } catch (error) {
        /* eslint-disable no-console */
        console.error('Error generating fingerprint:', error)
        /* eslint-enable no-console */
      }
    },
    methods: {
      ...call('prismic', ['loadSendABoxData']),
      resetForm() {
        this.formData = {
          friendFirstName: '',
          friendLastName: '',
          friendEmail: '',
          message: '',
        }
        this.submitStatus = null
      },

      async handleSubmit() {
        if (this.remainingBoxes <= 0) {
          this.submitStatus = 'error'
          return
        }

        try {
          this.isLoading = true
          this.submitStatus = null

          // If fingerprint wasn't generated in created hook, try again
          if (!this.visitorId) {
            try {
              const fp = await FingerprintJS.load()
              const result = await fp.get()
              this.visitorId = result.visitorId
            } catch (error) {
              /* eslint-disable no-console */
              console.error('Error generating fingerprint on submit:', error)
              /* eslint-enable no-console */
            }
          }

          // Push to dataLayer for GTM
          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({
            event: 'Send a Friend a Box',
            eventCategory: 'Referral',
            eventAction: 'Submit',
            friend_first_name: this.formData.friendFirstName,
            friend_last_name: this.formData.friendLastName,
            friend_email: this.formData.friendEmail,
            personal_message: this.formData.message || '',
            timestamp: new Date().toISOString(),
            visitor_fingerprint: this.visitorId || 'not_available',
            page_url: window.location.href,
            referrer: document.referrer || 'direct',
          })

          // Add referral to Craft
          const response = await fetch('/api/account/add-referral', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              friendEmail: this.formData.friendEmail,
              friendFirstName: this.formData.friendFirstName,
              friendLastName: this.formData.friendLastName,
              dateSent: new Date().toISOString().split('T')[0],
              message: this.formData.message || '',
            }),
          })

          const data = await response.json()

          if (!response.ok || data.error) {
            throw new Error(data.error || 'Failed to record referral')
          }

          this.remainingBoxes = data.remainingBoxes
          this.submitStatus = 'success'
        } catch (error) {
          /* eslint-disable no-console */
          console.error('Error:', error)
          /* eslint-enable no-console */
          this.submitStatus = 'error'
        } finally {
          this.isLoading = false
        }
      },
      scrollToSignUp() {
        const element = document.getElementById('sign-up')
        if (element) {
          const offset = 0 // Padding on top
          const elementPosition = element.getBoundingClientRect().top
          const offsetPosition = elementPosition + window.pageYOffset - offset

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth', // Smooth scroll animation
          })
        }
      },
      async loadUserReferralCount() {
        try {
          this.isLoadingReferrals = true
          const response = await fetch('/api/account/get-referrals', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
          })
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`)
          }
          const data = await response.json()
          this.remainingBoxes = data.remainingBoxes ?? null
        } catch (error) {
          /* eslint-disable no-console */
          console.error('Error loading referral count:', error)
          /* eslint-enable no-console */
        } finally {
          this.isLoadingReferrals = false
        }
      },
    },
  }
</script>

<template>
  <div>
    <div id="hero-banner" class="bg-primary md:flex items-stretch justify-center">
      <div id="hero-banner__image" class="md:w-1/2">
        <img
          :src="sendABoxContent.hero_image.url"
          class="w-full h-full object-cover"
          alt="Send a Box"
        />
      </div>
      <div
        id="hero-banner__content"
        class="text-dawn px-6 py-11 md:px-15 md:py-30 md:w-1/2 text-center flex flex-col items-center justify-center"
      >
        <PrismicRichText
          class="font-heading text-5xl md:text-8xl font-semibold mb-7 md:mb-12 leading-110"
          :field="sendABoxContent.hero_title"
        />

        <PrismicRichText
          class="font-heading text-xl md:text-4xl"
          :field="sendABoxContent.hero_subheader"
        />
        <!-- <button class="button button--summer">SEND AN INTRO BOX</button> -->
        <BaseButton
          type="link"
          color="sunshine"
          class="button inline-block mt-8 md:mt-12 uppercase"
          @click.prevent="scrollToSignUp"
        >
          {{ sendABoxContent.hero_button_text }}
        </BaseButton>
      </div>
    </div>
    <div id="how-it-works" class="custom-container text-center bg-dawn">
      <PrismicRichText
        class="font-heading text-6xl md:text-9xl font-semibold leading-110 mb-8"
        :field="sendABoxContent.how_it_works_header"
      />
      <PrismicRichText
        class="font-heading text-lg md:text-4xl mb-12 md:mb-20 max-w-250 mx-auto"
        :field="sendABoxContent.how_it_works_paragraph"
      />

      <ul class="how-it-works__steps grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-14">
        <li
          v-for="(step, index) in sendABoxContent.how_it_works_steps"
          :key="index"
          class="how-it-works__step max-w-60 justify-self-center"
        >
          <div class="aspect-1/1 mb-8">
            <img :src="step.step_image.url" class="w-full h-full object-cover" :alt="step.title" />
          </div>
          <PrismicRichText class="text-base md:text-lg" :field="step.step_description" />
        </li>
      </ul>
      <BaseButton
        type="link"
        color="sunshine"
        class="button inline-block mt-12 md:mt-19 uppercase"
        @click.prevent="scrollToSignUp"
      >
        {{ sendABoxContent.how_it_works_button_text }}
      </BaseButton>
    </div>
    <div id="sign-up" class="custom-container text-center bg-spring">
      <PrismicRichText
        class="font-heading text-6xl md:text-9xl font-semibold leading-110 mb-8"
        :field="sendABoxContent.sign_up_header"
      />
      <PrismicRichText
        class="font-heading text-lg md:text-4xl mb-8"
        :field="sendABoxContent.sign_up_sub_header"
      />
      <!-- <PrismicRichText
            class="text-base md:text-lg mb-12 md:mb-20 max-w-250 mx-auto"
            :field="sendABoxContent.sign_up_paragraph"
        /> OLD PRISMIC MESSAGE MADE DYNAMIC BELOW-->
      <div v-if="!isLoadingReferrals" v-html="referralMessage"></div>

      <!-- Loading Overlay -->
      <div
        v-if="isLoading"
        class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10"
      >
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>

      <!-- Success Message -->
      <div
        v-if="submitStatus === 'success'"
        class="bg-dawn p-6 md:p-12 rounded-custom max-w-250 mx-auto text-center"
      >
        <div class="mb-6">
          <svg
            class="w-16 h-16 mx-auto text-green-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 13l4 4L19 7"
            ></path>
          </svg>
          <h3 class="text-2xl font-heading font-semibold mt-4">Box Invitation Sent!</h3>
          <p class="mt-2">Your friend will receive an email invitation shortly.</p>
        </div>
        <BaseButton type="button" color="sunshine" class="button mt-7 md:mt-12" @click="resetForm">
          SEND ANOTHER BOX
        </BaseButton>
      </div>

      <!-- Error Message -->
      <div
        v-else-if="submitStatus === 'error'"
        class="bg-dawn p-6 md:p-12 rounded-custom max-w-250 mx-auto text-center"
      >
        <div class="mb-6">
          <svg
            class="w-16 h-16 mx-auto text-red-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            ></path>
          </svg>
          <h3 class="text-2xl font-heading font-semibold mt-4">Oops! Something went wrong</h3>
          <p class="mt-2"
            >Please try again or reach out to our customer service team at
            <a href="mailto:customer@alltrue.com" class="text-primary underline"
              >customer@alltrue.com</a
            >
            with any questions.</p
          >
        </div>
        <BaseButton type="button" color="sunshine" class="button mt-7 md:mt-12" @click="resetForm">
          TRY AGAIN
        </BaseButton>
      </div>

      <!-- Form -->
      <form
        v-else
        class="bg-dawn p-6 md:p-12 rounded-custom max-w-250 mx-auto"
        :class="{ 'opacity-50 pointer-events-none': remainingBoxes <= 0 }"
        @submit.prevent="handleSubmit"
      >
        <div class="form-group-row grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6 mb-6">
          <div class="form-group text-left">
            <input
              id="friends-first-name"
              v-model="formData.friendFirstName"
              class="block bg-white p-3 mb-3 w-full"
              type="text"
              name="friends-first-name"
              required
            />
            <label class="block" for="friends-first-name">Friend's First Name</label>
          </div>
          <div class="form-group text-left">
            <input
              id="friends-last-name"
              v-model="formData.friendLastName"
              class="block bg-white p-3 mb-3 w-full"
              type="text"
              name="friends-last-name"
              required
            />
            <label class="block" for="friends-last-name">Friend's Last Name</label>
          </div>
          <div class="form-group text-left">
            <input
              id="friends-email"
              v-model="formData.friendEmail"
              class="block bg-white p-3 mb-3 w-full"
              type="email"
              name="friends-email"
              required
            />
            <label class="block" for="friends-email">Friend's Email</label>
          </div>
        </div>
        <div class="form-group-row">
          <div class="form-group text-left">
            <textarea
              id="message"
              v-model="formData.message"
              class="block bg-white p-3 mb-3 w-full"
              maxlength="80"
              placeholder="up to 80 characters"
            ></textarea>
            <label class="block" for="message">Message</label>
          </div>
        </div>
        <BaseButton
          type="submit"
          color="sunshine"
          class="button mt-7 md:mt-12 uppercase"
          :disabled="isLoading"
        >
          {{ sendABoxContent.sign_up_button_text }}
        </BaseButton>
      </form>
    </div>
    <div
      id="discover-products"
      class="custom-container text-left md:text-center bg-primary text-dawn overflow-hidden"
    >
      <PrismicRichText
        class="font-heading text-6xl md:text-9xl font-semibold leading-110 mb-8"
        :field="sendABoxContent.product_discovery_title"
      />
      <PrismicRichText
        class="font-heading text-xl md:text-4xl mb-12 md:mb-20 max-w-250 mx-auto"
        :field="sendABoxContent.product_discovery_paragraph"
      />
      <!-- Desktop view -->
      <ul
        class="hidden md:grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6"
      >
        <li
          v-for="product in sendABoxContent.product_discovery_products"
          :key="product.id"
          class="flex flex-col"
        >
          <div class="relative">
            <img
              :src="product.product_image.url"
              class="w-full aspect-1/1 object-cover"
              :alt="product.title"
            />
            <div class="absolute top-2 right-2">
              <div class="star right-3 top-3">
                <p
                  class="text-black text-center text-left font-semibold text-lg font-heading leading-none"
                >
                  <span class="text-xs align-top inline-block">$</span>{{ product.product_price }}
                  <span class="block text-xs font-normal block text-xs">Value</span>
                </p>
              </div>
            </div>
          </div>
          <div class="mt-3 md:mt-4 text-center">
            <h3 class="text-sm md:text-sm font-semibold mb-4">{{ product.product_box }}</h3>
            <p class="text-sm md:text-sm mt-2"
              >{{ product.product_title }}<br />{{ product.product_description }}</p
            >
          </div>
        </li>
      </ul>

      <!-- Mobile view using BaseSlider -->
      <div class="md:hidden -mx-4 md:mx-0 mt-24">
        <div class="pl-4">
          <BaseSlider has-navigation :slides-per-view="1.66" class="product-slider">
            <BaseSliderSlide
              v-for="product in sendABoxContent.product_discovery_products"
              :key="product.id"
              class="product-slide"
            >
              <div class="flex flex-col">
                <div class="relative">
                  <img
                    :src="product.product_image.url"
                    class="w-full aspect-1/1 object-cover"
                    :alt="product.title"
                  />
                  <div class="absolute top-2 right-2">
                    <div class="star right-3 top-3">
                      <p
                        class="text-black text-center font-semibold text-lg font-heading leading-none"
                      >
                        <span class="text-xs align-top inline-block">$</span
                        >{{ product.product_price }}
                        <span class="block text-xs font-normal block text-xs">Value</span>
                      </p>
                    </div>
                  </div>
                </div>
                <div class="mt-3 md:mt-4 text-center">
                  <h3 class="text-sm md:text-sm font-semibold mb-4">{{ product.product_box }}</h3>
                  <p class="text-sm md:text-sm mt-2"
                    >{{ product.product_title }}<br />{{ product.product_description }}</p
                  >
                </div>
              </div>
            </BaseSliderSlide>
          </BaseSlider>
        </div>
      </div>
    </div>
    <!-- <div id="stay-in-touch">
        <h2>Stay in touch</h2>
        <p>Join our mailing list to receive updates on new products, exclusive offers, and more.</p>
        <form>
            <div class="form-group">
                <label class="sr-only" for="email">Enter your email</label>
                <input class="form-group__input" type="email" id="email" name="email" placeholder="Enter your email" />
            </div>
            <button>SUBSCRIBE</button>
        </form>
    </div> -->
    <div id="links" class="custom-container py-10">
      <ul class="flex flex-wrap justify-between gap-4 text-md font-regular">
        <li v-for="(link, index) in sendABoxContent.links" :key="index">
          <a :href="link.link" class="hover:underline">{{ link.link_text }}</a>
        </li>
      </ul>
    </div>
    <div class="custom-container text-center small-copy text-center bg-dawn py-4">
      <PrismicRichText :field="sendABoxContent.small_print_text" />
    </div>
    <div class="custom-container text-center small-copy text-center bg-black text-white">
      <img
        :src="sendABoxContent.footer_image.url"
        class="m-auto mb-6"
        :alt="sendABoxContent.footer_image.alt"
      />
      <PrismicRichText
        class="font-heading text-6xl md:text-9xl font-semibold leading-110 mb-8"
        :field="sendABoxContent.footer_header"
      />
      <ul class="flex flex-wrap justify-center gap-4 text-md font-regular mb-16">
        <li v-for="(icon, index) in sendABoxContent.footer_icons" :key="index">
          <a :href="icon.icon_link" class="inline-block">
            <img
              :src="icon.icon_image.url"
              :alt="icon.icon_image.alt"
              class="w-full h-full aspect-1/1 object-cover max-h-10"
            />
          </a>
        </li>
      </ul>

      <PrismicRichText
        class="text-md font-regular mt-6"
        :field="sendABoxContent.footer_additional_text"
      />
    </div>
  </div>
</template>

<style lang="postcss" scoped>
  .star {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 55px;
    height: 55px;
    padding: 0.5rem;
    margin: 0 auto;
    color: white;
    background-color: #ffff7d;
    border-radius: 12%;
    transform: rotate(45deg);
  }

  .star::before,
  .star::after {
    position: absolute;
    top: 0;
    left: 0;
    z-index: -1;
    display: block;
    width: 100%;
    height: 100%;
    content: '';
    background-color: #ffff7d;
    border-radius: 12%;
  }

  .star::before {
    transform: rotate(30deg);
  }

  .star::after {
    transform: rotate(60deg);
  }

  .star p {
    transform: rotate(-45deg);
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }

  .product-slider {
    & >>> {
      .swiper-container {
        @apply overflow-visible;

        margin: 0;
      }

      .swiper-wrapper {
        @apply ml-0;
      }

      .swiper-navigation {
        position: absolute;
        top: -40px;
        left: 24px;
      }

      .swiper-button-prev,
      .swiper-button-next {
        @apply border text-white -mt-8 z-10;
      }

      .swiper-button-prev {
        @apply right-1 top-1;
      }

      .swiper-button-next {
        @apply right-1 top-1;
      }

      .swiper-button-prev.swiper-button-disabled,
      .swiper-button-next.swiper-button-disabled {
        display: flex;
      }
    }
  }

  .product-slide {
    @apply pr-4;
  }
</style>
